import React, { useState, useRef } from 'react';
import axiosInstance from 'src/lib/axiosInstance';
import Layout from 'src/layout';
import Container from '@/components/ui/container';
import InputUi from '@/components/ui/inputui';
import UploadImages from '@/components/ui/uploadImages';
import SelectProducts from '@/components/ui/selectProducts';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RiArrowLeftLine, RiGiftLine, RiUploadCloudLine, RiShoppingBag3Line } from 'react-icons/ri';

const AddOffer = () => {
    const navigate = useNavigate();
    const [offerType, setOfferType] = useState('buy_x_get_y');
    const [selectedProducts, setSelectedProductsState] = useState([]);
    const [offerName, setOfferName] = useState('');
    const [buyCount, setBuyCount] = useState('');
    const [buyAt, setBuyAt] = useState('');
    const [getCount, setGetCount] = useState('');
    const [discountType, setDiscountType] = useState('percentage');
    const [discountValue, setDiscountValue] = useState('');
    const [productScope, setProductScope] = useState('different_product');
    const [submitting, setSubmitting] = useState(false);

    const mobileBannerRef = useRef();
    const desktopBannerRef = useRef();

    const handleOfferTypeChange = (e) => {
        setOfferType(e.target.value);
        if (e.target.value === 'buy_x_at_x' || e.target.value === 'buy_x_get_off') {
            setGetCount('');
        }
    };

    const handleToggleProductParent = (productID) => {
        setSelectedProductsState((prev) =>
            prev.includes(productID)
                ? prev.filter((id) => id !== productID)
                : [...prev, productID]
        );
    };

    const handleSubmit = async () => {
        if (!offerName.trim()) {
            toast.error('Please enter an Offer Name');
            return;
        }
        if (!buyCount || Number(buyCount) <= 0) {
            toast.error('Please enter a valid Buy Count');
            return;
        }

        try {
            setSubmitting(true);
            const [mobileBanner, desktopBanner] = await Promise.all([
                mobileBannerRef.current?.uploadImageFunction(),
                desktopBannerRef.current?.uploadImageFunction()
            ]);

            const payload = {
                offerName,
                offerType,
                buyCount: Number(buyCount),
                buyAt: offerType === 'buy_x_at_x' ? Number(buyAt) : null,
                getCount: offerType === 'buy_x_get_off' ? 0 : Number(getCount),
                discountType: offerType === 'buy_x_get_off' ? discountType : null,
                discountValue: offerType === 'buy_x_get_off' ? Number(discountValue) : null,
                productScope: offerType === 'buy_x_get_off' ? productScope : 'different_product',
                offerMobileBanner: mobileBanner?.[0]?.imgUrl || '',
                offerBanner: desktopBanner?.[0]?.imgUrl || '',
                products: selectedProducts
            };

            const res = await axiosInstance.post('/offer/add-offer', payload);
            const data = res.data;

            if (data.success) {
                toast.success(data.message || 'Offer created successfully');
                navigate('/offer/list');
            } else {
                toast.error(data.message || 'Failed to create offer');
            }
        } catch (error) {
            console.error('Error submitting offer:', error);
            toast.error(error.response?.data?.message || 'Failed to create offer');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout active="admin-offers-add" title="Add Offer">
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <button
                                onClick={() => navigate('/offer/list')}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-emerald-600 mb-2 transition-colors"
                            >
                                <RiArrowLeftLine className="w-4 h-4" /> Back to Offers
                            </button>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiGiftLine className="text-emerald-600" /> Add New Offer
                            </h1>
                            <p className="text-slate-500 text-sm mt-0.5">
                                Set up promotional rules, select applicable products, and attach promotional banners.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/offer/list')}
                                className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm min-w-[120px]"
                            >
                                {submitting ? 'Creating...' : 'Publish Offer'}
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main Product Selection Column */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Basic Info Card */}
                            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4">
                                <h2 className="text-base font-semibold text-slate-900 pb-2 border-b border-slate-100">
                                    Basic Offer Details
                                </h2>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                        Offer Title / Name *
                                    </label>
                                    <InputUi
                                        placeholder="e.g. Festival Buy 2 Get 1 Free"
                                        value={offerName}
                                        datafunction={(val) => setOfferName(val.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Product Selection Card */}
                            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4">
                                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                                    <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                                        <RiShoppingBag3Line className="text-emerald-600" /> Target Products
                                    </h2>
                                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                                        {selectedProducts.length} selected
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500">
                                    Choose products to include under this promotional offer rule.
                                </p>

                                <SelectProducts
                                    onProductToggle={handleToggleProductParent}
                                    initialSelected={selectedProducts}
                                />
                            </div>
                        </div>

                        {/* Side Configuration Column */}
                        <div className="space-y-6">
                            {/* Offer Rules Card */}
                            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4">
                                <h2 className="text-base font-semibold text-slate-900 pb-2 border-b border-slate-100">
                                    Offer Configuration Rules
                                </h2>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                        Offer Logic Type
                                    </label>
                                    <select
                                        value={offerType}
                                        onChange={handleOfferTypeChange}
                                        className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                    >
                                        <option value="buy_x_get_y">Buy X Get Y Free</option>
                                        <option value="buy_x_at_x">Buy X at Fixed Price (₹X)</option>
                                        <option value="buy_x_get_off">Buy X Get Discount (% / Flat OFF)</option>
                                    </select>
                                </div>

                                <InputUi
                                    label="Buy Requirement Count (X)"
                                    type="number"
                                    placeholder="e.g. 2"
                                    value={buyCount}
                                    datafunction={(val) => setBuyCount(val.target.value)}
                                />

                                {offerType === 'buy_x_at_x' && (
                                    <InputUi
                                        label="Fixed Special Price (₹)"
                                        type="number"
                                        placeholder="e.g. 999"
                                        value={buyAt}
                                        datafunction={(val) => setBuyAt(val.target.value)}
                                    />
                                )}

                                {offerType === 'buy_x_get_y' && (
                                    <InputUi
                                        label="Free Items Quantity (Y)"
                                        type="number"
                                        placeholder="e.g. 1"
                                        value={getCount}
                                        datafunction={(val) => setGetCount(val.target.value)}
                                    />
                                )}

                                {offerType === 'buy_x_get_off' && (
                                    <>
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                                Discount Unit
                                            </label>
                                            <select
                                                value={discountType}
                                                onChange={(e) => setDiscountType(e.target.value)}
                                                className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                            >
                                                <option value="percentage">Percentage (%) OFF</option>
                                                <option value="flat">Flat Amount (₹) OFF</option>
                                            </select>
                                        </div>

                                        <InputUi
                                            label={discountType === 'percentage' ? 'Discount Percentage (%)' : 'Flat Amount (₹)'}
                                            type="number"
                                            placeholder="e.g. 20"
                                            value={discountValue}
                                            datafunction={(val) => setDiscountValue(val.target.value)}
                                        />

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                                Product Scope Requirement
                                            </label>
                                            <select
                                                value={productScope}
                                                onChange={(e) => setProductScope(e.target.value)}
                                                className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                            >
                                                <option value="different_product">Mix & Match / Any Eligible Item</option>
                                                <option value="same_product">Same Product Only</option>
                                            </select>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Banners Upload Card */}
                            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4">
                                <h2 className="text-base font-semibold text-slate-900 pb-2 border-b border-slate-100 flex items-center gap-2">
                                    <RiUploadCloudLine className="text-emerald-600" /> Promotional Banners
                                </h2>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                        Mobile Banner Image
                                    </label>
                                    <UploadImages ref={mobileBannerRef} />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                        Desktop Banner Image
                                    </label>
                                    <UploadImages ref={desktopBannerRef} />
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default AddOffer;
